import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Campaign,
  CampaignDetailResponse,
  CampaignOccurrenceView,
  ProvenanceMap,
  StoreResponse,
} from '@clarity/contract';
import { PLATFORMS } from '@clarity/contract';
import { useCampaign } from '../../queries/index.js';
import { useScopeContextOptional } from '../../lib/scopeContext.js';
import { isNotYetOnboarded } from '../../lib/lifecycle.js';
import { isOnboardingRequired, num } from '../format.js';
import { ProfitModel, campaignDefaults, isAds, toWhatIfQuery, type WhatIf } from './ProfitModel.js';
import { BreakdownPivot } from './BreakdownPivot.js';
import { CpcBreakeven } from './CpcBreakeven.js';
import { DealEconomicsPanel } from './DealEconomicsPanel.js';
import {
  ConfidenceMeter,
  discountRangeLabel,
  occurrenceChips,
  occurrenceWindow,
} from './occurrence-shared.js';

/**
 * CampaignDetail (clarity-campaign-matching task 13.2, Req 9.3) — one campaign's
 * detail surface, rebuilt from the agreed mock `brand/campaigns-mock.html` (VIEW
 * 2 · CAMPAIGN DETAIL) in the teal-glass world. The mock is the contract: the
 * header (name + Arabic + source tag + terms), the MONTHLY-only enrollment-
 * inference note, the profit model, and the provenance strip are reproduced here
 * as real components over the typed ts-rest client.
 *
 * It is a sibling route of the six workspace surfaces under `store/:storeId`
 * (like {@link StorePayoutDetail}), reached from the list's row-open navigation
 * (task 13.1). The store comes from the scope context; a passed `store` prop wins
 * (tests mount it outside a ScopeProvider).
 *
 * SEAMS left for the sibling tasks, documented so they slot in without reshaping
 * this component:
 *  - the "See full breakdown" pivot + the ads CPC break-even are TASK 13.3 —
 *    {@link BreakdownPivot} (inline-expanding, inside the profit-model panel) +
 *    {@link CpcBreakeven} (ads-only sibling card);
 *  - the occurrences list + OccurrenceDetail are TASK 13.4 — the
 *    {@link OccurrencesSlot} placeholder sits where the occurrences list renders.
 *
 * The what-if sliders (in ProfitModel) drive `whatIf` state here, which folds
 * into `useCampaign(storeId, cid, whatIf)` so the SAME pure core recomputes the
 * projection server-side (Req 10.2). Co-funding is NOT a lever — pinned (Req 7.3).
 */
export function CampaignDetail({ store: storeProp }: { store?: StoreResponse }) {
  const { clientId, storeId, cid } = useParams();
  const navigate = useNavigate();

  const scope = useScopeContextOptional();
  const store = storeProp ?? scope?.store ?? null;
  const campaignId = cid ?? '';

  // The what-if the sliders own. Null until the campaign loads (we seed it from
  // the campaign's declared terms once we have them), so a slider drag folds into
  // the detail re-query without a round-trip on the first paint.
  const [whatIf, setWhatIf] = useState<WhatIf | null>(null);

  const enabled = store != null && !isNotYetOnboarded(store);

  // The base read (no what-if) always runs so we have the campaign + its declared
  // terms to seed the sliders and show the default projection.
  const base = useCampaign(store?.id ?? '', campaignId, undefined, enabled);
  const baseData: CampaignDetailResponse | null =
    base.data?.status === 200 ? base.data.body : null;

  // The live query folds the committed what-if (the keys this campaign drives).
  // When nothing is dirty it IS the base query (same key), so no extra fetch.
  const whatIfQuery = useMemo(
    () => (baseData && whatIf ? toWhatIfQuery(baseData.campaign, whatIf) : undefined),
    [baseData, whatIf],
  );
  const live = useCampaign(store?.id ?? '', campaignId, whatIfQuery, enabled && Boolean(whatIfQuery));
  const liveData: CampaignDetailResponse | null =
    whatIfQuery == null
      ? baseData
      : live.data?.status === 200
        ? live.data.body
        : baseData; // keep the last good model while the recompute lands

  const back = () => {
    if (clientId && storeId) navigate(`/c/${clientId}/store/${storeId}/campaigns`);
  };

  // A participated occurrence row opens its own detail page (task 13.4),
  // a sibling route under the same store scope.
  const openOccurrence = (occurrenceId: string) => {
    if (clientId && storeId) {
      navigate(`/c/${clientId}/store/${storeId}/campaigns/${campaignId}/occurrences/${occurrenceId}`);
    }
  };

  if (isOnboardingRequired(base.error)) return <Frame onBack={back}><p className="muted">This branch isn’t onboarded yet.</p></Frame>;
  if (base.isLoading) return <Frame onBack={back}><DetailLoading /></Frame>;
  if (base.isError || !baseData || !store) {
    return (
      <Frame onBack={back}>
        <p className="error" role="alert">Could not load this campaign.</p>
      </Frame>
    );
  }

  const campaign = baseData.campaign;
  const current = whatIf ?? campaignDefaults(campaign);
  const projection = (liveData ?? baseData).projection;
  const currency = store.profile?.currency ?? 'JOD';
  const recomputing = whatIfQuery != null && live.isFetching;

  return (
    <Frame onBack={back} title={campaign.name}>
      <DetailHeader campaign={campaign} currency={currency} />

      {campaign.source === 'monthly' && (
        <EnrollmentNote platformName={PLATFORMS[campaign.platformKey].name} />
      )}

      <section className="card panel pm__panel" aria-label="Profit model">
        <h3 className="pm__title">
          Will it pay off?
          <span className="pm__hint">
            {' '}
            · a forward profit model. Order mix, order value and conversion rate come from{' '}
            <b>this campaign’s own history</b> where it has run before, else this store’s profile;
            the store’s economics and the deal’s co-funding are fixed. Drag to test a change.
          </span>
        </h3>

        <ProfitModel
          campaign={campaign}
          projection={projection}
          whatIf={current}
          onWhatIf={setWhatIf}
          currency={currency}
          recomputing={recomputing}
        />

        <ProvenanceStrip provenance={projection.provenance} campaign={campaign} />

        {/* Task 13.3 — the inline-expanding "See full breakdown" pivot. The grid
            comes off the live projection, so it reshapes as the sliders move. */}
        <BreakdownPivot pivot={projection.pivot} campaign={campaign} currency={currency} />
      </section>

      {/* Task 13.3 — ads-only CPC break-even block (sibling card, per the mock);
          renders only when the projection carries a CPC break-even. */}
      {projection.cpc && <CpcBreakeven cpc={projection.cpc} currency={currency} />}

      {/* clarity-campaign-terms-economics task 6.3 — the "What this deal actually
          cost you" food-cost margin panel (sibling card, per the terms mock's
          `#v-lqta` view). Mounted ONLY for an item-scoped deal; a whole-menu
          campaign never carries scoped food-cost economics. */}
      {campaign.itemScoped && (
        <DealEconomicsPanel store={store} campaign={campaign} currency={currency} />
      )}

      {/* The occurrences list (task 13.4): each run of this campaign with its
          inferred participation (participated / not-enrolled / upcoming) +
          confidence. A participated row opens the OccurrenceDetail. */}
      <OccurrencesList
        occurrences={baseData.occurrences}
        campaign={campaign}
        currency={currency}
        onOpen={openOccurrence}
      />
    </Frame>
  );
}

/** The page frame: a Back link to the list + an accessible region. */
function Frame({
  onBack,
  title,
  children,
}: {
  onBack: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="campaigns campaign-detail" aria-label={title ? `Campaign — ${title}` : 'Campaign'}>
      <button type="button" className="back" onClick={onBack}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m15 18-6-6 6-6" />
        </svg>
        Campaigns
      </button>
      {children}
    </section>
  );
}

/**
 * The detail header (Req 9.3): the campaign name + its Arabic name + a source
 * tag (participation detected for admin-defined monthly offers, from API for
 * ads/platform) + the resolved terms line.
 */
function DetailHeader({ campaign, currency }: { campaign: Campaign; currency: string }) {
  const detected = campaign.provenance === 'admin';
  const sourceLabel =
    campaign.source === 'monthly'
      ? 'Monthly · defined by Clarity admin'
      : campaign.source === 'ads'
        ? 'Ads · from API'
        : 'Platform deal · from API';

  return (
    <header className="detail-head">
      <div>
        <h2 className="detail-head__title">
          {campaign.name}
          {campaign.arabicName && (
            <span className="detail-head__ar" dir="rtl">
              {campaign.arabicName}
            </span>
          )}
        </h2>
        <div className="detail-head__sub">
          {detected ? (
            <span className="badge campaign-detail__tag campaign-detail__tag--detected">
              Participation detected
            </span>
          ) : (
            <span className="badge campaign-detail__tag campaign-detail__tag--api">From API</span>
          )}
          <span>
            <span className={`src-dot src-${campaign.source}`} aria-hidden="true" />
            {sourceLabel}
          </span>
          <span>· {termsLine(campaign, currency)}</span>
        </div>
      </div>
    </header>
  );
}

/**
 * The MONTHLY-only enrollment-inference note (Req 9.3). Admin enters the offer
 * calendar; enrollment is the restaurant's own off-platform choice, so we don't
 * know in advance which occurrences ran — we infer it from the orders. Shown
 * ONLY for a `source:'monthly'` campaign; ads/platform participation is known
 * exactly from the API, so the note would be misleading there.
 */
function EnrollmentNote({ platformName }: { platformName: string }) {
  return (
    <div className="ev-note" role="note">
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </svg>
      <p>
        Your Clarity admin enters the monthly offer calendar from {platformName} (same for every store).
        Enrollment is the restaurant’s own choice, agreed with {platformName} off-platform — so we don’t
        know in advance which occurrences you ran. We infer that from your orders: an occurrence
        with matching orders counts as participated, one with none as not enrolled.
      </p>
    </div>
  );
}

/**
 * The provenance strip (Req 7.3): where each projection input came from, honest
 * per field. mix/AOV/CR resolve to the campaign's own history (teal) or the store
 * profile; co-funding is a FIXED deal term (violet, pinned — never inferred);
 * economics (markup / COGS / fees) is always the store profile. For an ads
 * campaign the CR line reads as the projected conversion rate instead of a
 * co-funding term.
 */
function ProvenanceStrip({
  provenance,
  campaign,
}: {
  provenance: ProvenanceMap;
  campaign: Campaign;
}) {
  const histLabel = (src: 'history' | 'store') =>
    src === 'history' ? ['this campaign’s history', 'hist'] as const : ['store profile', 'store'] as const;

  const items: Array<[string, string, string]> = [
    ['Order mix', ...histLabel(provenance.mix)],
    ['Order value / AOV', ...histLabel(provenance.aov)],
  ];
  if (isAds(campaign)) {
    const [v, cls] = histLabel(provenance.cr);
    items.push(['Conversion rate', provenance.cr === 'history' ? 'projected from history' : v, cls]);
  } else {
    items.push(['Co-funding %', 'fixed deal term', 'fixed']);
  }
  items.push(['Store economics (markup, COGS, fees)', 'store profile', 'store']);

  return (
    <dl className="prov" aria-label="Projection provenance">
      {items.map(([k, v, cls]) => (
        <div className="prov__item" key={k}>
          <span className="prov__dot" aria-hidden="true" />
          <dt className="prov__key">{k}:</dt>
          <dd className={`prov__src prov__src--${cls}`}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * The occurrences list (task 13.4, Req 9.4 / 6.5) — each dated run of this
 * campaign, rebuilt from the mock's `#cd-occ` timeline. Participation is
 * INFERRED FROM ORDERS, never declared: a run with matched orders is
 * `participated` (opens to its OccurrenceDetail), a past run with none is
 * `not_enrolled`, a future run is `upcoming`. Participated rows carry the
 * aggregate match confidence; the status is carried by a labelled chip (never
 * color alone — WCAG 1.4.1).
 *
 * A participated row is a real `<button>` (keyboard-reachable, announces the
 * open); the static rows are plain list items so they don't masquerade as
 * controls.
 */
function OccurrencesList({
  occurrences,
  campaign,
  currency,
  onOpen,
}: {
  occurrences: CampaignOccurrenceView[];
  campaign: Campaign;
  currency: string;
  onOpen: (occurrenceId: string) => void;
}) {
  if (occurrences.length === 0) return null;

  return (
    <section className="card panel occ-panel" aria-label="Occurrences">
      <h3 className="occ-panel__title">
        Occurrences
        <span className="pm__hint">
          {' '}
          · each run of this campaign. The restaurant enrolls per occurrence; we infer which ones
          ran from your orders. Participated runs open to the matched orders.
        </span>
      </h3>
      <ul className="occ" aria-label="Occurrences of this campaign">
        {occurrences.map((view) => (
          <OccurrenceRow
            key={view.occurrence.id}
            view={view}
            campaign={campaign}
            currency={currency}
            onOpen={onOpen}
          />
        ))}
      </ul>
    </section>
  );
}

/** One occurrence row — the date window, its overrides + confidence, and the
 *  participation chip. Participated rows are clickable buttons; the rest static. */
function OccurrenceRow({
  view,
  campaign,
  currency,
  onOpen,
}: {
  view: CampaignOccurrenceView;
  campaign: Campaign;
  currency: string;
  onOpen: (occurrenceId: string) => void;
}) {
  const { occurrence, status, matchedCount, confidence } = view;
  const window = occurrenceWindow(occurrence);
  const chips = occurrenceChips(occurrence, campaign, currency);

  const when = (
    <span className="occ__when">
      <span className="occ__dates num">{window.range}</span>
      <span className="occ__month">{window.month}</span>
    </span>
  );

  const meta = (
    <span className="occ__over">
      {chips.length === 0 ? (
        <span className="over-chip">no overrides</span>
      ) : (
        chips.map((c) => (
          <span key={c} className="over-chip changed">
            {c}
          </span>
        ))
      )}
      {status === 'participated' && confidence && <ConfidenceMeter level={confidence} />}
    </span>
  );

  const fig =
    status === 'participated' ? (
      <span className="occ__fig">
        <span className="occ__fig-n num">
          {num(matchedCount)} <small>orders</small>
        </span>
      </span>
    ) : status === 'not_enrolled' ? (
      <span className="occ__fig occ__fig--muted">
        <small>0 matched orders</small>
      </span>
    ) : null;

  const chip = <ParticipationChip status={status} />;

  if (status === 'participated') {
    return (
      <li className="occ__item">
        <button
          type="button"
          className="occ__row"
          onClick={() => onOpen(occurrence.id)}
        >
          {when}
          {meta}
          <span className="occ__end">
            {fig}
            {chip}
            <svg viewBox="0 0 24 24" width="16" height="16" className="occ__chev" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </span>
        </button>
      </li>
    );
  }

  return (
    <li className="occ__item">
      <div className="occ__row occ__row--static">
        {when}
        {meta}
        <span className="occ__end">
          {fig}
          {chip}
        </span>
      </div>
    </li>
  );
}

/**
 * The participation chip (Req 6.5). The status is carried by its TEXT, not just
 * the hue, so it is accessible without color (WCAG 1.4.1). The three states map
 * to the mock's teal "Participated", muted "Not enrolled", amber "Upcoming".
 */
function ParticipationChip({ status }: { status: CampaignOccurrenceView['status'] }) {
  if (status === 'participated') {
    return (
      <span className="badge occ-chip occ-chip--in">
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" />
        </svg>
        Participated
      </span>
    );
  }
  if (status === 'not_enrolled') {
    return <span className="badge occ-chip occ-chip--out">Not enrolled · no orders</span>;
  }
  return <span className="badge occ-chip occ-chip--up">Upcoming</span>;
}

function DetailLoading() {
  return (
    <div className="card campaigns__loading" role="status" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading campaign…</span>
      <span className="sk" style={{ inlineSize: '55%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '85%' }} aria-hidden="true" />
      <span className="sk" style={{ inlineSize: '70%' }} aria-hidden="true" />
    </div>
  );
}

// ── Pure helpers ──────────────────────────────────────────────────────────────

/** The resolved terms line for the header. */
function termsLine(campaign: Campaign, currency: string): string {
  if (isAds(campaign)) {
    const model = campaign.pricingModel ? campaign.pricingModel.toUpperCase() : 'ad';
    return campaign.cpc ? `${model} · ${num(campaign.cpc)} per click · search placement` : `${model} placement`;
  }
  const parts: string[] = [];
  const disc = discountRangeLabel(campaign);
  if (disc !== null) parts.push(`${disc} off all items`);
  if (campaign.maxDiscountCap !== null) parts.push(`cap ${num(campaign.maxDiscountCap)} ${currency}`);
  parts.push(
    campaign.audience === 'all' ? 'all customers' : campaign.audience === 'plus' ? 'Plus (TPRO) only' : 'non-Plus only',
  );
  return parts.join(', ');
}
